<template>
    <div class="articleListingLayout__vue">
        <template v-if="loading && !loaded">
            <div class="articleListingLayout__loading">
                <svg viewBox="0 0 100 100">
                    <path d="M73,50c0-12.7-10.3-23-23-23S27,37.3,27,50 M30.9,50c0-10.5,8.5-19.1,19.1-19.1S69.1,39.5,69.1,50"><animateTransform attributeName="transform" attributeType="XML" type="rotate" dur="1s" from="0 50 50" to="360 50 50" repeatCount="indefinite"></animateTransform></path>
                </svg>
            </div>

            <div class="pills pills--radio pills--loading articleListingLayout__filters">
                <div v-for="i in 4"
                     :key="i"
                     class="pill pill--radio pill--loading articleListingLayout__filter"
                >&nbsp;</div>
            </div>

            <div class="articleListingLayout__listing">
                <div v-for="i in 8"
                     :key="i"
                     class="articleListingLayout__listingLoadingChild"
                />
            </div>

            <div class="articleListingLayout__loadMore articleListingLayout__loadMore--loading">
                <ul class="paginationList">
                    <li v-for="i in 6"
                        :key="i"
                        class="paginationList__li"
                    >
                        <span class="paginationList__link paginationList__link--loading">&nbsp;</span>
                    </li>
                </ul>
            </div>
        </template>

        <template v-else>
            <div v-if="loading" class="articleListingLayout__loading">
                <svg viewBox="0 0 100 100">
                    <path d="M73,50c0-12.7-10.3-23-23-23S27,37.3,27,50 M30.9,50c0-10.5,8.5-19.1,19.1-19.1S69.1,39.5,69.1,50"><animateTransform attributeName="transform" attributeType="XML" type="rotate" dur="1s" from="0 50 50" to="360 50 50" repeatCount="indefinite"></animateTransform></path>
                </svg>
            </div>

            <div v-if="filter.categories && filter.categories.length" class="pills pills--radio articleListingLayout__filters">
                <a class="pill pill--radio articleListingLayout__filter"
                   :class="{ 'pill--active': selected_category === 'all' }"
                   :href="`${window_location_details}`"
                   @click.prevent="pillClickEvent('all')"
                >View all</a>

                <a v-for="(option, i) in filter.categories"
                   :key="i"
                   class="pill pill--radio articleListingLayout__filter"
                   :class="{ 'pill--active': selected_category === option.slug }"
                   :href="`${window_location_details}?category=${option.slug}`"
                   @click.prevent="pillClickEvent(option.slug)"
                >{{ option.name }}</a>
            </div>

            <div v-if="loaded && posts.length == 0" class="articleListingLayout__listing">
                <article class="articleListingLayout__noListings">
                    <h1 class="articleListingLayout__noListingsTitle">No posts found</h1>

                    <p class="articleListingLayout__noListingsDescription">Please choose different filters</p>
                </article>
            </div>

            <div v-if="loaded && posts.length > 0" class="articleListingLayout__listing">
                <template v-if="getListingType === 'case-study'">
                    <CaseStudyCard v-for="(post, i) in posts"
                                   :key="i"
                                   :url="post.url"
                                   :title="post.title"
                                   :categories="post.categories"
                                   :img="{
                                       url: post.img,
                                       height: 383,
                                       width: 588
                                   }"
                                   modifier="listing"
                    />
                </template>

                <template v-if="getListingType === 'post'">
                    <NewsCard v-for="(post, i) in posts"
                              :key="i"
                              :url="post.url"
                              :title="post.title"
                              :categories="post.categories"
                              :img="{
                                  url: post.img,
                                  height: 284,
                                  width: 546
                              }"
                              :colour="post.colour"
                    />
                </template>
            </div>

            <div v-if="pagination.pages_total >= 2"
                 class="articleListingLayout__loadMore articleListingLayout__pagination"
            >
                <Paginate
                    :model-value="page"
                    :page-count="pagination.pages_total"
                    :page-range="3"
                    :margin-pages="2"
                    :hide-prev-next="true"
                    :click-handler="paginationCallback"
                    container-class="paginationList"
                    page-class="paginationList__li"
                    page-link-class="paginationList__link"
                    prev-class="paginationList__li--prev"
                    prev-link-class="paginationList__link--prev"
                    next-class="paginationList__li--next"
                    next-link-class="paginationList__link--next"
                    break-view-class="paginationList__li--ellipsis"
                    break-view-link-class="paginationList__link--ellipsis"
                    active-class="paginationList__li--active"
                    disabled-class="paginationList__li--disabled"
                    :current-base-url="window_location_details"
                    :url-query-vars="selected_category !== 'all' ? `category=${selected_category}` : ''"
                />
            </div>
        </template>
    </div>
</template>

<script>
import NewsCard from './news_card.vue';
import CaseStudyCard from './case_study_card.vue';
import Paginate from './paginate.vue';

export default {
    props: {
        getCategory: {
            type: String,
            default: 'all'
        },
        getListingType: {
            type: String,
            default: 'case-study'
        },
        getPage: {
            type: Number,
            default: 1
        },
    },
    components: {
        NewsCard,
        CaseStudyCard,
        Paginate
    },
    data() {
        return {
            posts: [],
            filter: [],
            page: 1,
            pagination: {
                total_results: 0,
                pages_total: 0,
                per_page: 8
            },
            loaded: false,
            loading: false, // show loading overlay
            update_page: true, // for disabling watch on pop state
            selected_category: this.getCategory,
            previous_request: null
        }
    },
    computed: {
        window_location_details() {
            return `${window.location.protocol}//${window.location.hostname}${window.location.pathname}`;
        }
    },
    created() {
        this.getFilters();

        // Attach onpopstate event handler
        window.onpopstate = (event) => {
            if (event.state && event.state.response) {
                this.posts = [];
                this.$nextTick(() => {
                    Array.prototype.push.apply(this.posts, event.state.response.posts || []);
                    this.pagination = event.state.response.pagination || {
                        total_results: 0,
                        pages_total: 0,
                        per_page: 8
                    };
                    this.update_page = false;
                    this.selected_category = event.state.response.selected_category;

                    this.$nextTick(() => {
                        this.update_page = true;
                    });
                });
            }
        };
    },
    methods: {
        getPosts(update_url = false) {
            let url = window.local.ajax_url,
                data = {
                    action: 'get_articles',
                    category: this.selected_category,
                    page : this.page,
                    type: this.getListingType
                };

            this.loading = true;

            $.ajax({
                url: url,
                method: 'post',
                data: data,
                dataType: 'json',
                context: this,
                beforeSend: (request) => {
                    // abort previous request, if exists
                    if (this.previous_request) this.previous_request.abort();

                    // set previous request on Vue instance
                    this.previous_request = request;
                },
                success: (response) => {
                    if (update_url) this.posts = [];

                    this.$nextTick(() => {
                        Array.prototype.push.apply(this.posts, response.posts || []);
                        this.loading = false;

                        if (update_url) {
                            this.loaded = true;
                            this.pagination = response.pagination || {
                                total_results: 0,
                                pages_total: 0,
                                per_page: 8
                            };
                            this.updateURL(response); // update url
                        }
                    });
                }
            });
        },
        getFilters() {
            let url = window.local.ajax_url,
                data = {
                    action: 'get_articles',
                    filters: true,
                    type: this.getListingType
                };

            this.loading = true;

            $.ajax({
                url: url,
                method: 'post',
                data: data,
                dataType: 'json',
                success: (response) => {
                    let responseFilters = JSON.parse(JSON.stringify(response.filter))
                    
                    if (
                        typeof responseFilters.categories === 'object' &&
                        !Array.isArray(responseFilters.categories) &&
                        responseFilters.categories !== null
                    ) responseFilters.categories = Object.values(response.filter.categories)

                    this.filter = responseFilters || [];
                    this.getPosts(true);
                }
            });
        },
        updateURL(response) {
            let query_string = '',
                query_sign = '?',
                url = window.location.origin + window.location.pathname;

            if (this.selected_category !== 'all') {
                query_string += query_sign + 'category=' + this.selected_category;
                query_sign = '&';
            }

            if (this.page !== 1) {
                query_string += query_sign + 'page-value=' + this.page;
                query_sign = '&';
            }

            window.history.pushState(
                {
                    'pageTitle': document.title,
                    'response': {
                        selected_category: this.selected_category,
                        page: this.page
                    }
                },
                '',
                url + query_string
            );
        },
        paginationCallback(pageNum) {
            if (this.page !== pageNum) this.page = pageNum;
        },
        scroll_browser() {
            window.scrollTo({
                top: 0,
                left: 0,
                behavior: 'auto'
            });
        },
        pillClickEvent(string) {
            if (this.selected_category !== string) this.selected_category = string;
        }
    },
    watch: {
        selected_category() {
            this.page = 1;
            if (this.update_page) {
                this.scroll_browser();
                this.loaded = false;
                this.getPosts(true);
            }
        },
        page() {
            if (this.update_page) {
                this.scroll_browser();
                this.loaded = false;
                this.getPosts(true);
            }
        }
    }
}
</script>
